import { usePrefetchPLPQuery } from 'client/api/plp-results';
import { useCallback, useMemo, useRef } from 'react';

const defaultURLGetter = (e: React.MouseEvent<HTMLAnchorElement> | React.FocusEvent<HTMLAnchorElement>) =>
  e.currentTarget.href;

const usePrefetchProps = (
  urlGetter: (
    e: React.MouseEvent<HTMLAnchorElement> | React.FocusEvent<HTMLAnchorElement>,
  ) => string | undefined = defaultURLGetter,
  timeout = 200,
) => {
  const prefetchPLPQuery = usePrefetchPLPQuery();
  const timeoutRef = useRef<number | null>(null);

  const handlePrefetch = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement> | React.FocusEvent<HTMLAnchorElement>) => {
      const url = urlGetter(e);
      if (!url) return;

      timeoutRef.current = window.setTimeout(async () => {
        timeoutRef.current = null;
        await prefetchPLPQuery(url);
      }, timeout);
    },
    [prefetchPLPQuery, timeout, urlGetter],
  );

  const cancelPrefetch = useCallback(() => {
    if (timeoutRef.current === null) return;
    window.clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
  }, []);

  return useMemo(
    () => ({
      onMouseEnter: handlePrefetch,
      onMouseLeave: cancelPrefetch,
      onFocus: handlePrefetch,
      onBlur: cancelPrefetch,
      onTouchStart: handlePrefetch,
      onTouchEnd: cancelPrefetch,
    }),
    [cancelPrefetch, handlePrefetch],
  );
};

export default usePrefetchProps;
